<script lang="ts">
  import { CloudUpload, XIcon } from '@lucide/svelte'
  import type { HTMLInputAttributes } from 'svelte/elements'

  type Props = HTMLInputAttributes & {
    placeholder?: string
    error?: string
    sizeVariant?: 'sm' | 'md'
  }

  let {
    placeholder,
    error,
    sizeVariant = 'md',
    ...inputProps
  }: Props = $props()

  let fileInput = $state<HTMLInputElement>()
  let selectedFiles = $state<FileList | null>(null)
  let selectedFilesNames = $derived.by(() => {
    if (selectedFiles === null) return ''
    const files = Array.from(selectedFiles)
    return files.map(f => f.name).join(', ')
  })

  const onChange: HTMLInputAttributes['onchange'] = (e) => {
    selectedFiles = (e.target as HTMLInputElement).files
    inputProps.onchange?.(e)
  }

  const onClear = () => {
    if (fileInput?.value) {
      fileInput.value = ''
    }
    selectedFiles = null
  }
</script>

<div
  class="input-container"
  class:input-container--disabled={inputProps.disabled}
  class:input-container--error={error}
  class:input-container--sm={sizeVariant === 'sm'}
>
  <label class="input-label">
    <div class="input-description">
      <CloudUpload size="1rem" />
      {#if selectedFilesNames}
        <p class="files-names">{selectedFilesNames}</p>
      {:else if placeholder}
        <p class="placeholder">{placeholder}</p>
      {/if}
    </div>
    <input
      {...inputProps}
      bind:this={fileInput}
      aria-label={placeholder}
      onchange={onChange}
      type="file"
    />

    {#if error}
      <p class="error">{error}</p>
    {/if}
  </label>
  {#if selectedFilesNames}
    <button
      class="clear-button"
      onclick={onClear}
      type="button"
    >
      <XIcon size=".75rem" />
    </button>
  {/if}
</div>

<style lang="scss">
  .input-container {
    background: var(--input-background);
    display: flex;
    border-radius: 0.25rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    transition: all 0.3s ease;

    &:hover {
      background: var(--input-hover-background);
    }

    .input-label {
      flex: 1;
      cursor: pointer;
      padding: 0.5rem;

      .input-description {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;

        .placeholder {
          font-size: 0.75rem;
          color: var(--input-placeholder);
        }

        .files-names {
          color: var(--input-foreground);
          font-size: 0.75rem;
        }
      }
    }

    .clear-button {
      background: none;
      border: 0;
      padding: 0.5rem;
      display: flex;
      color: var(--input-placeholder);

      &:hover {
        color: var(--input-foreground);
        cursor: pointer;
      }
    }

    label,
    .error {
      line-height: 150%;
      font-size: 0.75rem;
      color: var(--foreground);
    }

    .error {
      color: var(--red);
      display: none;
    }

    input {
      display: none;
    }

    &--sm {
      .input-label,
      .clear-button {
        padding: 0.25rem;
      }
    }
  }
</style>
